import { useState, useEffect, useRef } from "react";

/**
 * Interactive 5-engine showcase. Click an engine pill → expand into a mini
 * mockup explaining what makes that engine "Omega-only." Auto-rotates every
 * 5.5s when nothing has been clicked.
 */

const ENGINES = [
  {
    id: "mc",
    label: "Monte Carlo",
    icon: "Σ",
    color: "#60a5fa",
    headline: "10,000 future paths, every 60 seconds.",
    sub: "Numpy-vectorized jump-diffusion. Most apps quote one Black-Scholes price. Omega simulates 10,000 paths and aggregates probability of profit across 5 horizons.",
    metric: "10,000",
    metricLabel: "paths / contract",
  },
  {
    id: "claude",
    label: "Claude Reasoning",
    icon: "Ψ",
    color: "#a78bfa",
    headline: "An AI quant reads every trade.",
    sub: "Claude Sonnet 4.6 cross-checks the deterministic verdict. If the math says BUY but the news flow disagrees, Omega flags the conflict — then explains why in plain English.",
    metric: "<800ms",
    metricLabel: "deep-dive latency",
  },
  {
    id: "guardian",
    label: "Guardian",
    icon: "◉",
    color: "#4ade80",
    headline: "A trader who never blinks.",
    sub: "Guardian polls your portfolio every 60s. If a position breaches its risk envelope (theta burn, IV crush, drawdown), it pushes a verdict-graded alert before your phone buzzes for the news.",
    metric: "60s",
    metricLabel: "watch interval",
  },
  {
    id: "trim",
    label: "Auto-Trim",
    icon: "✂",
    color: "#fbbf24",
    headline: "Exits without your finger on the trigger.",
    sub: "When conviction crosses 0.70, Omega trims 25%. At 0.85 it closes the position. No second-guessing, no thrash. Override anytime — but you almost never will.",
    metric: "0.70 → 25%",
    metricLabel: "trim threshold",
  },
  {
    id: "digest",
    label: "Replay Digest",
    icon: "↻",
    color: "#22d3ee",
    headline: "Every Sunday, your week, replayed.",
    sub: "Auto-generated post-mortem on every closed trade — top lessons, calibration drift, recurring risks. Emailed Sunday 9am ET. Lessons fold straight back into next week's Zi weights.",
    metric: "Sunday 9am",
    metricLabel: "weekly digest",
  },
];

export default function EngineShowcase() {
  const [active, setActive] = useState("mc");
  const [paused, setPaused] = useState(false);
  const ivRef = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    ivRef.current = setInterval(() => {
      setActive(prev => {
        const i = ENGINES.findIndex(e => e.id === prev);
        return ENGINES[(i + 1) % ENGINES.length].id;
      });
    }, 5500);
    return () => clearInterval(ivRef.current);
  }, [paused]);

  const e = ENGINES.find(x => x.id === active) || ENGINES[0];

  return (
    <section data-testid="engine-showcase" style={{
      padding: "70px 24px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box",
    }}>
      <div style={{ fontSize: 11, color: "#a78bfa", letterSpacing: 2, textAlign: "center", fontWeight: 700, marginBottom: 8 }}>
        WHAT OTHERS DON'T HAVE
      </div>
      <h2 style={{ fontSize: 32, fontWeight: 800, textAlign: "center", marginBottom: 8, letterSpacing: -0.6 }}>
        Five engines. One brain.
      </h2>
      <p style={{ fontSize: 14, color: "#8b949e", textAlign: "center", maxWidth: 620, margin: "0 auto 36px" }}>
        Robinhood shows you a price. Webull shows you a chart. <strong style={{ color: "#e6edf3" }}>Omega runs five live engines</strong> in parallel — and explains every move.
      </p>

      {/* Engine pills */}
      <div
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginBottom: 28 }}
      >
        {ENGINES.map(eng => {
          const isActive = eng.id === active;
          return (
            <button
              key={eng.id}
              data-testid={`engine-pill-${eng.id}`}
              onClick={() => setActive(eng.id)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 16px", borderRadius: 999,
                background: isActive ? `${eng.color}20` : "rgba(255,255,255,0.03)",
                border: `1px solid ${isActive ? eng.color + "88" : "rgba(255,255,255,0.08)"}`,
                color: isActive ? eng.color : "#8b949e",
                cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                transition: "all 220ms ease",
                boxShadow: isActive ? `0 0 24px ${eng.color}44` : "none",
              }}
            >
              <span style={{
                width: 24, height: 24, borderRadius: 8,
                background: `${eng.color}26`, color: eng.color,
                display: "grid", placeItems: "center", fontSize: 14, fontWeight: 700,
              }}>{eng.icon}</span>
              {eng.label}
            </button>
          );
        })}
      </div>

      {/* Detail card */}
      <div
        data-testid="engine-detail"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{
          maxWidth: 720, margin: "0 auto", padding: 28, borderRadius: 22,
          background: `linear-gradient(155deg, ${e.color}10, rgba(2,6,23,0.4))`,
          border: `1px solid ${e.color}40`,
          backdropFilter: "blur(12px)",
          boxShadow: `0 30px 80px rgba(2,6,23,0.55), 0 0 0 1px ${e.color}20 inset`,
          position: "relative", overflow: "hidden",
        }}
      >
        {/* Glow blob */}
        <div style={{
          position: "absolute", top: -80, right: -80, width: 240, height: 240,
          borderRadius: "50%", background: `radial-gradient(circle, ${e.color}33, transparent 70%)`,
          pointerEvents: "none",
        }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, position: "relative" }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: `linear-gradient(135deg, ${e.color}, ${e.color}88)`,
            color: "#fff", display: "grid", placeItems: "center",
            fontSize: 22, fontWeight: 800,
            boxShadow: `0 12px 30px ${e.color}55`,
          }}>{e.icon}</div>
          <div>
            <div style={{ fontSize: 9, color: e.color, letterSpacing: 2, fontWeight: 700 }}>OMEGA · ENGINE</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#e6edf3", letterSpacing: -0.4 }}>{e.label}</div>
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#e6edf3", letterSpacing: -0.4, marginBottom: 12, lineHeight: 1.25 }}>
          {e.headline}
        </div>
        <div style={{ fontSize: 14, color: "#8b949e", lineHeight: 1.6, marginBottom: 22 }}>
          {e.sub}
        </div>
        <div style={{
          display: "inline-flex", alignItems: "baseline", gap: 8,
          padding: "12px 18px", borderRadius: 14,
          background: "rgba(0,0,0,0.32)", border: `1px solid ${e.color}33`,
        }}>
          <span style={{ fontSize: 26, fontWeight: 800, color: e.color, letterSpacing: -0.5 }}>{e.metric}</span>
          <span style={{ fontSize: 11, color: "#8b949e", letterSpacing: 1 }}>{e.metricLabel.toUpperCase()}</span>
        </div>
      </div>
    </section>
  );
}
